/**
 * PLM — the v13 on-demand re-read rules for ONE user (prototype
 * PLM-Analysis-v13 `seeable` / `blindFor` / `lockReasonBase` / `newQc`).
 *
 * The SERVER is authoritative (PlmLayerRules + dbo.AI_Plm_AddReRead_SP refuse
 * anything these allow by mistake); this module only lets the screen show the
 * same answer up front — hide what the user may not see, grey what they may not
 * change, and offer only the re-reads they may start.
 *
 * `me` is the signed-in user's Oid, which IS their Employee Oid (CLAUDE.md
 * Rule C: Employee.Oid == PermissionPolicyUser.Oid), so it compares directly
 * with a layer's analyst.
 */
import {
  PLM_PENDING_VALIDATION, type PlmBatchHeader, type PlmCheckPoint, type PlmLayerRow, type PlmQcHistoryRow,
} from './plm.api';
import type { SdmsCheckPointEdit } from '../SdmsValidationActions';
import { buildQcPairs, groupLayers } from './plmQc';

const same = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

/** The layer's owner: a re-read's analyst, else whoever analysed it. */
export const ownerOf = (l: PlmLayerRow) => l.reReadAnalystOid ?? l.analyzedByOid ?? null;

export const isSampleLayer = (l: PlmLayerRow) => !l.reReadKind && (l.qcType ?? 'Sample') === 'Sample';

/**
 * Which QC dup flow the screen follows (owner decision 2026-10-17) — the server's
 * AI_AppSettings 'PlmDupSubmitButtons' switch, sent as `lookups.dupSubmitButtons`:
 *  - 'On' (`simple: false`) — today's flow: Start Inter-dup, Submit Read, Re-open Read;
 *  - anything else (`simple: true`, the default) — dups are entered like sample layers:
 *    no Start / Submit Read / Re-open Read, an Inter-dup is any analyst's but the
 *    original analyst's (the first to save it owns it), and while the batch is OPEN
 *    (`open`) the original and Intra-dup of a group with an Inter-dup are blind to
 *    everyone but the original analyst. The batch Submit closes the dup reads.
 * Every rule below that differs takes it as its last, optional argument; without it
 * the rule is today's ('On').
 */
export interface DupFlow {
  simple: boolean;
  /** The batch is open for result entry (not submitted) — nothing is blind after. */
  open: boolean;
}

/** The flow a lookups payload asks for. A server that does not send the switch (an
 *  older API) runs today's flow, so `undefined` reads as 'On'. */
export const dupFlowOf = (dupSubmitButtons: boolean | undefined, batchOpen: boolean): DupFlow =>
  ({ simple: dupSubmitButtons === false, open: batchOpen });

/**
 * Who may SEE a layer in the sequence (v13 `seeable`): an Inter re-read stays
 * hidden from everyone but its analyst until it is submitted. Every other layer
 * is listed (a blind one is listed but its content is withheld — see isBlind).
 * In the SIMPLE flow the sample's original analyst sees it too (they see everything).
 */
export function seeable(l: PlmLayerRow, me: string | null, layers?: readonly PlmLayerRow[], flow?: DupFlow): boolean {
  if (l.reReadKind !== 'Inter') return true;
  // A re-read the Analysis Queue built that nobody has started yet holds no
  // result to hide, and an eligible analyst has to be able to find it.
  if (!ownerOf(l)) return true;
  // SIMPLE (owner 2026-10-17, G5 parity — PlmV2Rules.Seeable): its analyst and the original
  // analyst see it while the batch is open; everyone after the Submit. The server applies it too.
  if (flow?.simple) return !flow.open || same(ownerOf(l), me) || (!!layers && same(originalAnalyst(l, layers), me));
  return !!l.reReadSubmitted || same(ownerOf(l), me);
}

/**
 * Blind in the SIMPLE flow (owner decision 2026-10-17; the server's
 * PlmLayerRules.IsSimpleBlind): a group that HAS an Inter-dup hides its original
 * layer(s) and Intra-dup from every user but the group's original analyst — Admins
 * included — whether or not anybody has read the Inter-dup yet. The caller checks the
 * batch is open.
 */
export function isSimpleBlind(l: PlmLayerRow, layers: readonly PlmLayerRow[], me: string | null): boolean {
  if (!me || !l.groupKey || l.reReadKind === 'Inter') return false;
  if (!layers.some(x => x.groupKey === l.groupKey && x.reReadKind === 'Inter')) return false;
  // Blind unless the user IS the original analyst. While nobody has entered the original
  // yet it is OPEN (owner 2026-10-19 - an Inter-dup added on the QC Batch form before
  // analysis hid the sample from everyone); the first analyst to save it becomes its original.
  const original = originalAnalyst(l, layers);
  return !!original && !same(original, me);
}

/**
 * Blind (v13 `blindFor`): the user has a PENDING Inter re-read in this layer's
 * group, so every OTHER layer of the group — the original and its Intra re-read
 * — is withheld until they submit. The SIMPLE flow: {@link isSimpleBlind} while
 * the batch is open, nothing after.
 */
export function isBlind(l: PlmLayerRow, layers: readonly PlmLayerRow[], me: string | null, flow?: DupFlow): boolean {
  if (flow?.simple) return flow.open && isSimpleBlind(l, layers, me);
  if (!me || !l.groupKey) return false;
  return layers.some(x => x.sequenceOid !== l.sequenceOid && x.groupKey === l.groupKey
    && x.reReadKind === 'Inter' && same(ownerOf(x), me) && !x.reReadSubmitted);
}

/** The Inter re-read that blinds this layer for the user, if any. In the SIMPLE
 *  flow: the group's Inter-dup when it is this user's (or nobody's yet) to read. */
export function blindingReRead(
  l: PlmLayerRow, layers: readonly PlmLayerRow[], me: string | null, flow?: DupFlow,
): PlmLayerRow | undefined {
  if (!me || !l.groupKey) return undefined;
  if (flow?.simple) {
    // Not blind while nobody has entered the original yet (owner 2026-10-19), as isSimpleBlind.
    if (l.reReadKind !== 'Inter' && !originalAnalyst(l, layers)) return undefined;
    return layers.find(x => x.groupKey === l.groupKey && x.reReadKind === 'Inter'
      && (!ownerOf(x) || same(ownerOf(x), me)));
  }
  return layers.find(x => x.sequenceOid !== l.sequenceOid && x.groupKey === l.groupKey
    && x.reReadKind === 'Inter' && same(ownerOf(x), me) && !x.reReadSubmitted);
}

/**
 * Why the user may NOT change this layer, or null (v13 `lockReasonBase`, in
 * the same order as the server's PlmLayerRules.EditBlock). `me` null = the
 * service account, which is exempt.
 */
export function lockReason(
  l: PlmLayerRow, layers: readonly PlmLayerRow[], me: string | null, owner?: string | null, flow?: DupFlow,
): string | null {
  if (!me) return null;
  if (flow?.simple) return simpleLockReason(l, layers, me, owner, flow);
  if (isBlind(l, layers, me)) return 'Blind analysis — this layer is hidden until you submit your Inter-dup read.';
  // Owner rule (2026-09-29): an Intra dup is performed by the SAME analyst as
  // the original layer, an Inter dup by a DIFFERENT analyst — also for a re-read
  // the Analysis Queue built with no analyst yet.
  if (l.reReadKind === 'Intra') {
    const first = originalAnalyst(l, layers);
    if (!first) return 'Enter the original layer first — an Intra-dup is read by the same analyst who analyzed it.';
    if (!same(first, me)) return `An Intra-dup is read by the original analyst (${owner ?? 'another analyst'}) — only they can enter it.`;
    // Submit Read closes the read (owner rule 2026-10-07); Re-open Read
    // (audited) is the way back in — the server's "intra-submitted".
    return l.reReadSubmitted && l.qcId ? 'This Intra-dup read is submitted — press Re-open Read to change it (audited).' : null;
  }
  if (l.reReadKind === 'Inter') {
    if (analysedBatch(layers, me)) return 'You performed the original analysis — the Inter-dup must be read by another analyst.';
    // Added by the original analyst and not started by anyone yet: STARTING it
    // is what makes the read blind (owner rule 2026-10-07), so it is not
    // entered before that.
    if (isUnclaimedInter(l)) return 'This Inter-dup has not been started — press Start Inter-dup to read it (blind).';
    const o = ownerOf(l);
    if (o && !same(o, me)) return `This Inter-dup is assigned to ${owner ?? 'another analyst'} for blind analysis — only they can enter it.`;
    // Only an explicit Submit Read locks it (a PLM re-read record — qcId). A
    // queue-built re-read reads "Entered" after its first save and must stay
    // editable by its analyst, as the server has it.
    if (l.reReadSubmitted && l.qcId) return 'This Inter-dup read is submitted — press Re-open Read to change it (audited).';
    return null;
  }
  const o = l.analyzedByOid;
  // No second full stop after an initial ("Ky N.").
  const who = owner ?? 'another analyst';
  return o && !same(o, me)
    ? `Read-only — ${l.sampleL ?? 'this layer'} is analyzed by ${who}${who.endsWith('.') ? '' : '.'}`
    : null;
}

/** The blind text of the SIMPLE flow (no read of one's own to submit). */
export const SIMPLE_BLIND_TEXT =
  'Blind analysis — this layer is hidden until the batch is submitted; only its original analyst sees it.';

/**
 * {@link lockReason} in the SIMPLE flow (owner decision 2026-10-17; the server's
 * PlmLayerRules.EditBlock with simple): no text names a Start / Submit Read /
 * Re-open Read step — an unowned Inter-dup is simply open to any analyst but the
 * original one, and a submitted batch is changed through its Rollback.
 */
function simpleLockReason(
  l: PlmLayerRow, layers: readonly PlmLayerRow[], me: string, owner: string | null | undefined, flow: DupFlow,
): string | null {
  if (isBlind(l, layers, me, flow)) return SIMPLE_BLIND_TEXT;
  if (l.reReadKind === 'Intra') {
    const first = originalAnalyst(l, layers);
    if (!first) return 'Enter the original layer first — an Intra-dup is read by the same analyst who analyzed it.';
    return same(first, me) ? null
      : `An Intra-dup is read by the original analyst (${owner ?? 'another analyst'}) — only they can enter it.`;
  }
  if (l.reReadKind === 'Inter') {
    if (same(originalAnalyst(l, layers), me)) {
      return 'You performed the original analysis of this sample — its Inter-dup must be read by another analyst.';
    }
    const o = ownerOf(l);
    return o && !same(o, me)
      ? `This Inter-dup is assigned to ${owner ?? 'another analyst'} for blind analysis — only they can enter it.`
      : null;
  }
  const o = l.analyzedByOid;
  const who = owner ?? 'another analyst';
  return o && !same(o, me)
    ? `Read-only — ${l.sampleL ?? 'this layer'} is analyzed by ${who}${who.endsWith('.') ? '' : '.'}`
    : null;
}

/**
 * The Daily QC lock (prototype v10–v13 `lockReason`): until today's reference-
 * slide read passes — or a supervisor overrides a failed one — every layer the
 * user would enter (their own, or one nobody has started) is read-only. Layers
 * owned by someone else are already locked by {@link lockReason}. The server
 * refuses the same writes (403 daily-qc-required).
 */
export function dailyLockReason(l: PlmLayerRow, me: string | null, dailyOk: boolean): string | null {
  if (!me || dailyOk) return null;
  const owner = ownerOf(l);
  return !owner || same(owner, me)
    // The prototype's wording, verbatim (v13 lockReasonBase): the bar with the
    // button IS at the top of the sequence.
    ? 'Daily reference check not passed today — read your reference slide first (top of the sequence).'
    : null;
}

/** The analyst of the ORIGINAL sample layer of this layer's group. */
export function originalAnalyst(l: PlmLayerRow, layers: readonly PlmLayerRow[]): string | null {
  return layers.find(x => x.groupKey === l.groupKey && isSampleLayer(x))?.analyzedByOid ?? null;
}

/** Whose name a lock on this layer mentions: an Intra's original analyst,
 *  otherwise the layer's owner. */
export const lockOwnerOid = (l: PlmLayerRow, layers: readonly PlmLayerRow[]) =>
  l.reReadKind === 'Intra' ? originalAnalyst(l, layers) : ownerOf(l);

/**
 * Who may be put in a layer's Analyzed By (owner rule 2026-09-29), from the
 * analyst list — the server's PlmLayerRules.AnalystBlock, mirrored:
 *  - an Intra dup — only the original layer's analyst;
 *  - an Inter dup — anyone who did NOT analyse the batch's sample layers;
 *  - an ORIGINAL layer — its Intra dup's analyst once that is read, and never
 *    anyone who read an Inter dup on the batch (else a queue-built Inter read
 *    first, or an original re-assigned later, would break the rule from the
 *    other side). Its current analyst always stays allowed, as on the server.
 */
export function allowedAnalyst(
  l: PlmLayerRow | null, layers: readonly PlmLayerRow[], analystOid: string, flow?: DupFlow,
): boolean {
  if (!l) return true;
  if (l.reReadKind === 'Intra') {
    const first = originalAnalyst(l, layers);
    return !first || same(first, analystOid);
  }
  // The SIMPLE flow (2026-10-17): anybody but the original analyst of THIS sample.
  if (l.reReadKind === 'Inter') {
    return flow?.simple ? !same(originalAnalyst(l, layers), analystOid) : !analysedBatch(layers, analystOid);
  }
  if (same(l.analyzedByOid, analystOid)) return true;
  const intraBy = l.groupKey
    ? layers.map(x => (x.groupKey === l.groupKey && x.reReadKind === 'Intra' ? ownerOf(x) : null)).find(Boolean)
    : null;
  if (intraBy && !same(intraBy, analystOid)) return false;
  // Any Inter-dup of the batch, in both flows (owner 2026-10-17, G5 parity: AllowedAnalyst).
  return !layers.some(x => x.reReadKind === 'Inter' && same(ownerOf(x), analystOid));
}

/** Every sample layer is entered — the batch is ready for its Interdup. */
export const allSamplesEntered = (layers: readonly PlmLayerRow[]) => {
  const samples = layers.filter(isSampleLayer);
  return samples.length > 0 && samples.every(l => l.status === 2);
};

/** Did the user analyse any sample layer of the batch? (then no Interdup). */
export const analysedBatch = (layers: readonly PlmLayerRow[], me: string | null) =>
  !!me && layers.some(l => isSampleLayer(l) && same(l.analyzedByOid, me));

const hasReRead = (layers: readonly PlmLayerRow[], l: PlmLayerRow, kind: 'Intra' | 'Inter') =>
  layers.some(x => x.groupKey === l.groupKey && x.reReadKind === kind);

/** An Inter dup row nobody has started: the original analyst added it (or the
 *  queue built it) and it waits for a second analyst (owner rule 2026-10-07). */
export const isUnclaimedInter = (l: PlmLayerRow) => l.reReadKind === 'Inter' && !ownerOf(l) && !l.qcId;

/** Layers the user may start an INTRA dup on: their own sample layers that
 *  have none yet (v13 `intraS`). Dups are selected AFTER analysis (owner rule
 *  2026-10-07), so there are none until every sample layer is entered. */
export function intraCandidates(layers: readonly PlmLayerRow[], me: string | null): PlmLayerRow[] {
  if (!me || !allSamplesEntered(layers)) return [];
  return layers.filter(l => isSampleLayer(l) && same(l.analyzedByOid, me) && !hasReRead(layers, l, 'Intra'));
}

/** Why the user may not READ an Inter-dup of this batch, or null. */
export function interdupBlock(layers: readonly PlmLayerRow[], me: string | null): string | null {
  if (!me) return 'Sign in as an analyst to read an Inter-dup.';
  if (!allSamplesEntered(layers)) return 'An Inter-dup can be read once the first analyst has entered every sample layer.';
  if (analysedBatch(layers, me)) return 'You performed the original analysis — the Inter-dup must be read by another analyst.';
  return null;
}

/** The Inter-dups a second analyst may START reading (owner rule 2026-10-07):
 *  the dup rows the ORIGINAL analyst added that nobody has started yet. The
 *  second analyst does not pick the sample — they take a dup that is waiting. */
export function interCandidates(layers: readonly PlmLayerRow[], me: string | null): PlmLayerRow[] {
  if (interdupBlock(layers, me)) return [];
  return layers.filter(isUnclaimedInter);
}

/** The short on-screen name of a dup. The QC types were renamed "Intra-dup" /
 *  "Inter-dup" (2026-10-07); a layer code or QC type that still carries the
 *  older "… Re-read" wording reads the same way. Display only. */
export const dupLabel = (text: string | null | undefined) =>
  (text ?? '').replace(/(Intra|Inter)\s*Re-?read/gi, '$1-dup');

/**
 * Why a dup may NOT be added on a layer from the Sequence list's right-click
 * menu, or null (owner rule 2026-10-07: both dups are selected on the entry
 * page, after analysis, by the analyst who analysed the sample). Checked in the
 * order the analyst can act on them; the server refuses the same things.
 */
export function dupBlock(
  source: PlmLayerRow, layers: readonly PlmLayerRow[], me: string | null, kind: 'Intra' | 'Inter',
  analystName?: string | null,
): string | null {
  const code = source.sampleL ?? 'this layer';
  if (!isSampleLayer(source)) return "A dup is added on a sample layer — right-click the sample's own row.";
  if (!allSamplesEntered(layers)) return 'Dups are selected after analysis — enter every sample layer of the batch first.';
  if (hasReRead(layers, source, kind)) return `${code} already has its ${kind}-dup.`;
  // `me` null = the service account, which the server answers for.
  if (me && !same(source.analyzedByOid, me)) {
    return `Only the original analyst (${analystName ?? "the layer's analyst"}) can add a dup on ${code}.`;
  }
  return null;
}

/**
 * Why "Add Intra-dup / Add Inter-dup" does not apply to a row, in a few words
 * (null = it does) — the grey note beside the item in the Sequence list's
 * right-click menu (prototype v18). Same rules as {@link dupBlock}.
 */
export function dupMenuHint(
  row: PlmLayerRow, layers: readonly PlmLayerRow[], me: string | null, kind: 'Intra' | 'Inter', locked: boolean,
): string | null {
  if (locked) return 'batch locked';
  if (!isSampleLayer(row)) return 'right-click a sample layer';
  if (hasReRead(layers, row, kind)) return 'already added';
  if (!allSamplesEntered(layers)) return 'enter every layer first';
  if (me && !same(row.analyzedByOid, me)) return 'not your sample';
  return null;
}

/**
 * Why "Claim Inter-dup" does not apply to a row (null = it does) — the
 * prototype v18 right-click item on an OPEN Inter-dup: a second analyst takes
 * it and reads it blind. Same rules as the Inter-dup bar's Start Inter-dup
 * ({@link interdupBlock}); the server re-checks them and the permission.
 */
export function claimDupHint(
  row: PlmLayerRow, layers: readonly PlmLayerRow[], me: string | null, locked: boolean,
): string | null {
  if (!isUnclaimedInter(row)) return 'already started';
  if (locked) return 'batch locked';
  if (!me) return null;
  if (!allSamplesEntered(layers)) return 'enter every layer first';
  if (analysedBatch(layers, me)) return 'you are the first analyst';
  return null;
}

/**
 * Why "Remove dup" does not apply to a row (null = it does): only a QC dup not
 * yet submitted, by its own analyst or the sample's analyst (Bin Yu 2026-10-15).
 */
export function removeDupHint(
  row: PlmLayerRow, layers: readonly PlmLayerRow[], me: string | null, locked: boolean, flow?: DupFlow,
): string | null {
  if (!row.reReadKind) return 'right-click a dup layer';
  if (locked) return 'batch locked';
  // The SIMPLE flow closes a read only by the batch Submit: on an open batch (rolled
  // back since) a closed read is removable like any other.
  if (row.reReadSubmitted && !flow?.simple) return 'read submitted';
  if (!me) return null;
  // The dup's owner = its re-read analyst, else whoever entered it (owner 2026-10-17, G5 OwnerOid).
  return same(ownerOf(row), me) || same(originalAnalyst(row, layers), me) ? null : 'not your dup';
}

/**
 * The top-bar title's hover text (prototype v13 `#tbTitle.title`): the batch,
 * its job(s) and how many layers the user can see in the sequence — "QC Batch
 * QB-260827-04 · Job 260827002 · 6 in sequence". `jobIds` is the batch's
 * `;`-joined Jobid; a batch over several jobs reads "Jobs a, b".
 */
export function batchTitleTooltip(batchId: string, jobIds: string | null | undefined, inSequence: number): string {
  const jobs = (jobIds ?? '').split(/[;,]/).map(j => j.trim()).filter(Boolean);
  const parts = [`QC Batch ${batchId}`];
  if (jobs.length) parts.push(`${jobs.length > 1 ? 'Jobs' : 'Job'} ${jobs.join(', ')}`);
  parts.push(`${inSequence} in sequence`);
  return parts.join(' · ');
}

/** The title of a PLM LIST, per mode (prototype v13 :2877). */
export const plmListTitle = (mode: 'view' | 'enter' | 'validate' | 'qc') =>
  mode === 'qc' ? 'PLM – QC Results' : mode === 'validate' ? 'PLM – Validate' : 'PLM – QC Batches';

// ── v13 Validate mode (2026-09-29) ──────────────────────────────────────────

/**
 * Validate mode ON/OFF. ON since 2026-10-10 (owner decision); it was OFF from
 * 2026-09-30 while it waited for approval. The flag only shows or hides the
 * "Validate" tab of the PLM mode switch, which is the mode's one entry point
 * (the QC Batch form opens PLM in Enter mode). SDMS Result Validation keeps
 * working either way — both screens call the same validate / rollback
 * endpoints, so a batch ends in the same state from either.
 */
export const PLM_VALIDATE_MODE_ENABLED = true;

/**
 * G5's Retrieve bar above the batch's action bar (Select Mode · QC Batch ID · Job ID ·
 * Retrieve). HIDDEN for now (owner request 2026-10-16); the bar and its code stay —
 * set this back to true to show it again.
 */
export const PLM_RETRIEVE_BAR_ENABLED = false;

/** The server's wording (AnalysisWriter.PlmSegregationMessage), verbatim. */
export const PLM_SELF_VALIDATE_MSG = 'You analyzed this PLM batch — another user must validate it.';
export const PLM_NOT_PENDING_MSG = 'This batch is not pending validation.';

/**
 * Why the user may NOT validate this batch, or null — the SERVER's rule
 * (AnalysisWriter.ValidateOneAsync) mirrored so the button can say so up front:
 * the batch must be Pending SDMS Validation, and while Default Settings → "No
 * Self-Review" is on nobody who analysed a layer of it — original or re-read —
 * may validate it. Prototype v13 blocks self-validation always; this follows
 * the lab's setting, exactly as SDMS Result Validation does (owner decision
 * 2026-09-29: "Follow Default Settings").
 */
export function validateBlock(
  status: number | null | undefined, layers: readonly PlmLayerRow[], me: string | null, noSelfReview: boolean,
): string | null {
  if (status !== PLM_PENDING_VALIDATION) return PLM_NOT_PENDING_MSG;
  if (noSelfReview && me && layers.some(l => same(l.analyzedByOid, me))) return PLM_SELF_VALIDATE_MSG;
  return null;
}

/** A check point's answer as stored: one of Yes / No / NA, or none yet. */
export function checkPointDecision(r: PlmCheckPoint): 'Yes' | 'No' | 'NA' | '' {
  return r.yes ? 'Yes' : r.no ? 'No' : r.na ? 'NA' : '';
}

/** A check point as the validator currently sees it — the stored value
 *  overlaid with their unsaved answer. */
export function effectiveCheckPoint(r: PlmCheckPoint, edit: SdmsCheckPointEdit | undefined) {
  return {
    decision: edit?.decision ?? checkPointDecision(r),
    qualifier: edit?.qualifier ?? r.qualifier ?? '',
    comment: edit?.comment ?? r.comment ?? '',
  };
}

/** How many check points still have no Yes / No / NA — SDMS Validate refuses
 *  the batch while any do (AnalysisWriter.ValidateOneAsync gate 2). */
export const unansweredCheckPoints = (rows: readonly PlmCheckPoint[], edits: Map<string, SdmsCheckPointEdit>) =>
  rows.filter(r => !effectiveCheckPoint(r, edits.get(r.id)).decision).length;

/** The same rule from the LIST, which knows each batch's first analyst but not
 *  every layer's — so it catches the common case and the server catches the
 *  rest (an Interdup analyst, say) and says which batch it skipped. */
export function batchValidateBlock(b: PlmBatchHeader, me: string | null, noSelfReview: boolean): string | null {
  if (b.status !== PLM_PENDING_VALIDATION) return PLM_NOT_PENDING_MSG;
  if (noSelfReview && me && same(b.firstAnalystOid, me)) return PLM_SELF_VALIDATE_MSG;
  return null;
}

// ── v13 QC Results list ─────────────────────────────────────────────────────

export type QcResult = 'Pass' | 'Fail' | 'Pending' | 'Blind';

/** One row of the QC Results list (prototype v13 `qcRecords`). */
export interface QcRecord {
  key: string;
  /** QC-yyMMdd-NNNN for a re-read started from PLM, else the re-read's layer. */
  qcId: string;
  batchOid: string | null;
  batchId: string | null;
  /** 'Reference' = a Daily QC read of a reference slide (v13). */
  type: 'Intra-dup' | 'Inter-dup' | 'Reference';
  /** The layer that was re-read, or the slide that was read. */
  sample: string;
  /** "first analyst → re-read analyst". */
  analysts: string;
  date: string | null;
  result: QcResult;
  notBlind: boolean;
  /** Set when the dup breaks the analyst rule (owner, 2026-09-29) — an Inter
   *  dup read by the original analyst, or an Intra dup read by someone else.
   *  The pair already FAILS on it (plmQc's analyst check); this says why. */
  ruleBreak?: 'same analyst' | 'different analyst' | null;
}

/**
 * The QC Results list from the lab-wide history (the SAME pairing and verdict
 * the QC Result View tab uses — plmQc.ts), seen by ONE user:
 *   · another analyst's Inter re-read is not listed until it is submitted;
 *   · a pair whose original this user is blind to reads "Blind";
 *   · a re-read not submitted yet reads "Pending".
 */
export function qcRecords(rows: readonly PlmQcHistoryRow[], me: string | null, simple = false): QcRecord[] {
  const meta = new Map(rows.map(r => [r.sequenceOid, r]));
  const key = (r: PlmQcHistoryRow) => `${r.batchOid}|${r.sampleLogInOid}|${r.layerLetter}`;
  // Pending Inter re-reads of THIS user blind the rest of their group, per batch.
  // The SIMPLE flow (2026-10-17): a group whose Inter-dup is still open (its batch is
  // not submitted — the Submit closes the read) is blind to all but the original analyst.
  // (The server's qc-history?view=records already applies this — PlmLayerRules.QcRecordsView —
  // and marks the blind rows; the rows' own `blind` is honoured below.)
  const blindGroups = new Set<string>();
  for (const r of rows) {
    if (r.qcType !== 'Inter Re-read' || r.reReadSubmitted) continue;
    if (simple) {
      // G5: blind unless the user is the original analyst of the sample (or there is none).
      const first = rows.find(o => key(o) === key(r) && (o.qcType ?? 'Sample') === 'Sample')?.analyzedByOid;
      if (!same(first, me)) blindGroups.add(key(r));
    } else if (me && same(r.reReadAnalystOid, me)) {
      blindGroups.add(key(r));
    }
  }
  const out: QcRecord[] = [];
  for (const pair of buildQcPairs(groupLayers(rows as never))) {
    const m = meta.get(pair.reRead.sequenceOid);
    if (!m) continue;
    const inter = pair.kind === 'Inter';
    const mine = !!me && same(m.reReadAnalystOid, me);
    // Someone else's open Inter read is not listed — in BOTH flows; the user's own read is
    // never "Blind" (owner 2026-10-17, G5 parity: PlmV2Rules.QcRecords).
    if (inter && !m.reReadSubmitted && !mine) continue;
    const blind = !mine && (!!m.blind || blindGroups.has(key(m)));
    const result: QcResult = blind ? 'Blind'
      : !m.reReadSubmitted || pair.state === 'pending' ? 'Pending'
        : pair.state === 'pass' ? 'Pass' : 'Fail';
    const o = pair.original?.analyzedByOid, r = pair.reRead.analyzedByOid;
    const ruleBreak = result === 'Blind' || !o || !r ? null
      : inter && same(o, r) ? 'same analyst'
        : !inter && !same(o, r) ? 'different analyst' : null;
    out.push({
      key: pair.reRead.sequenceOid,
      qcId: m.qcId ?? pair.reRead.sampleLayer ?? '—',
      batchOid: m.batchOid,
      batchId: m.batchId,
      type: inter ? 'Inter-dup' : 'Intra-dup',
      sample: pair.original?.sampleLayer ?? '—',
      analysts: `${pair.original?.analyzedByName ?? '—'} → ${pair.reRead.analyzedByName ?? '—'}`,
      date: m.analyzedDate,
      result,
      notBlind: !!m.notBlind,
      ruleBreak,
    });
  }
  return out.sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
}

/** Short tone for the batch-stage chip. */
export function stageTone(stage: string | null | undefined): 'warn' | 'info' | 'ok' {
  if (stage === 'Ready to Submit') return 'ok';
  if (stage === 'Pending Interdup' || stage === 'Interdup In Progress') return 'info';
  return 'warn';
}
